import {
  DeviceOS,
  DeviceType,
  LYTEFLO_LOGIN_ENABLED_KEY,
  Platform,
  PWA_INSTALLATION_SOURCE_KEY,
} from "@constants/environment";
import { PortalRoutes } from "@lyteflo/types";
import type { BasicAuthCredentials } from "@lyteflo/utils";
import { getCookieByName } from "@utils/http";

export const isLocalDev = import.meta.env.MODE === "localdev";
export const isDev = import.meta.env.MODE === "development";
export const isStaging = import.meta.env.MODE === "staging";
export const isProd = import.meta.env.MODE === "production";

export const validateEnvVarsExist = () => {
  const requiredEnvVars = [
    "VITE_SIGNIN_REDIRECT_URI",
    "VITE_BASIC_AUTH_USER",
    "VITE_BASIC_AUTH_PASSWORD",
    "VITE_FLAGSMITH_ENVIRONMENT_ID",
    "VITE_CDN_BASE_URL",
    "VITE_API_BASE_URL",
    "VITE_API_AUTH",
    "VITE_API_CONFIGURATION",
    "VITE_API_DEVICE",
    "VITE_API_INVENTORY",
    "VITE_API_REPORT",
    "VITE_API_LEAD",
    "VITE_WS_BASE_URL",
    "VITE_WS_PATH",
    "VITE_AMPLITUDE_API_KEY",
  ];

  const missingEnvVars = requiredEnvVars.filter(
    (envVar) => !import.meta.env[envVar],
  );

  if (missingEnvVars.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missingEnvVars.join(", ")}`,
    );
  }
};

export const getFullBaseUrl = () => {
  return window.location.origin;
};

export const getAppBaseUrlOverride = (): string => {
  const devOverride = import.meta.env.VITE_APP_BASE_URL_DEV;
  if (isLocalDev && devOverride) {
    return devOverride;
  }
  return window.location.origin;
};

export const resolveLocalDevUrl = (url: string): string => {
  if (!isLocalDev) {
    return url;
  }
  try {
    const original = new URL(url);
    const base = new URL(getAppBaseUrlOverride());
    return `${base.origin}${original.pathname}${original.search}`;
  } catch (_) {
    return url;
  }
};

export const getBasicAuthCredentials = (): BasicAuthCredentials => {
  return {
    username: import.meta.env.VITE_BASIC_AUTH_USER,
    password: import.meta.env.VITE_BASIC_AUTH_PASSWORD,
  };
};

export const getLoginRoute = (): string => {
  if (isLytefloLoginEnabled()) {
    return getFullBaseUrl() + PortalRoutes.LYTEFLOGIN;
  }
  return getFullBaseUrl() + PortalRoutes.LOGIN;
};

export const isLytefloLoginEnabled = (): boolean => {
  return (
    typeof window !== "undefined" &&
    localStorage.getItem(LYTEFLO_LOGIN_ENABLED_KEY) === "true"
  );
};

export const setLytefloLoginPreference = (useLytefloLogin: boolean): void => {
  try {
    if (useLytefloLogin) {
      localStorage.setItem(LYTEFLO_LOGIN_ENABLED_KEY, "true");
    } else {
      localStorage.removeItem(LYTEFLO_LOGIN_ENABLED_KEY);
    }
  } catch {
    // ignore
  }
};

export const getDevicePlatform = (): Platform | null => {
  const userAgent = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return Platform.IOS;
  }
  if (/Android/.test(userAgent)) {
    return Platform.ANDROID;
  }
  return null;
};

export const getDeviceInfo = (): {
  device_type: DeviceType;
  device_os: DeviceOS;
} => {
  if (typeof navigator === "undefined") {
    return { device_type: DeviceType.DESKTOP, device_os: DeviceOS.IOS };
  }

  const platform = getDevicePlatform();
  const ua = navigator.userAgent;

  let device_os: DeviceOS = DeviceOS.IOS;
  if (platform === Platform.ANDROID) device_os = DeviceOS.ANDROID;
  else if (/Mac|Macintosh/.test(ua)) device_os = DeviceOS.MAC;
  else if (/Win|Windows/.test(ua)) device_os = DeviceOS.WINDOWS;

  const device_type: DeviceType =
    platform === Platform.IOS || platform === Platform.ANDROID
      ? DeviceType.MOBILE
      : DeviceType.DESKTOP;

  return { device_type, device_os };
};

export const isStandalonePwa = () => {
  return (
    (typeof window !== "undefined" &&
      window.matchMedia("(display-mode: standalone)").matches) ||
    // @ts-expect-error - iOS standalone property
    window.navigator.standalone === true
  );
};

export const getEnvDisplayedName = () => {
  if (isLocalDev) {
    return "local";
  }
  if (isDev) {
    return "development";
  }
  if (isStaging) {
    return "staging";
  }
  return "";
};

export const handlePwaInstallDetection = () => {
  const installationSource = getCookieByName(PWA_INSTALLATION_SOURCE_KEY);
  if (installationSource === "lyteflo") {
    setLytefloLoginPreference(true);
    clearPwaLytefloInstallCookie();
  }
};

export const setPwaLytefloInstallCookie = () => {
  document.cookie = `${PWA_INSTALLATION_SOURCE_KEY}=lyteflo; path=/; max-age=86400`;
};

export const clearPwaLytefloInstallCookie = () => {
  document.cookie = `${PWA_INSTALLATION_SOURCE_KEY}=; path=/; max-age=0`;
};
